import {
  InlineDatumReactComponentProps,
  useAnalytics,
  useInlineDataEvents,
} from '@stripe-internal/inline-data';
import {useEffect, useRef, useState} from 'react';
import {ChatButtonOptions, TypingRow} from '@stripe-internal/conversations-ui';
import {view} from '@sail/ui/deprecated';
import {defineMessages, useIntl} from 'react-intl';
import {v4 as uuid} from 'uuid';

export type ValueProps = {
  email_confirmed?: boolean;
  email_send_count: number;
  selected_option_id?: string;
  email_just_resent?: boolean;
};

const messages = defineMessages({
  noAccessOption: {
    id: 'mse_inline_data.email_verification.noaccess',
    description: 'Option to select when user does not have access to email',
    defaultMessage: "I don't have access to this email",
  },
  didntReceiveOption: {
    id: 'mse_inline_data.email_verification.didnt_receive',
    description: 'Option to select when user did not receive email',
    defaultMessage: "I didn't receive the email",
  },
  resend: {
    id: 'mse_inline_data.email_verification.resend',
    description: 'Option to resend email',
    defaultMessage: 'Resend Email',
  },
});

const POLL_MS = 3000;

const EMAIL_CONFIRMED = 'confirmed';
const EMAIL_NO_ACCESS = 'noaccess';
const EMAIL_RESEND = 'resend';

export const EmailVerification = (props: InlineDatumReactComponentProps) => {
  const {datum, action, fetch, onDone, isDone} = props;
  const value = datum.value as ValueProps;
  const analytics = useAnalytics();

  const {
    email_confirmed: emailConfirmed,
    email_send_count: emailSendCount,
    selected_option_id: selectedOptionId,
    email_just_resent: emailJustResent,
  } = value;

  const events = useInlineDataEvents();

  const nonce = uuid();

  // Initial email and subsequent resend occured already
  const hasResend = emailSendCount > 1;

  const fetchInitialState = useRef<boolean>(true);
  const sendCount = useRef<number>(emailSendCount);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedOption, setSelectedOption] = useState<string | undefined>();
  const [emailResent, setEmailResent] = useState<boolean>(false);
  const [emailIsConfirmed, setEmailIsConfirmed] = useState<boolean>(false);

  useEffect(() => {
    if (selectedOptionId) {
      setSelectedOption(selectedOptionId);
      if (!isDone) {
        onDone({
          type: 'success',
          message: selectedOptionId,
          extra: {
            outcome: selectedOptionId,
            value,
          },
        });
      }
    }
  }, [isDone, onDone, selectedOption, selectedOptionId, value]);

  useEffect(() => {
    // Ensure that we have the most recent state upon initial render
    if (fetchInitialState.current) {
      // We need to pass the 'selectedOptionId' through our props
      // to ensure the backend passes it back to us in when it sets the props again
      fetch({poll: true, selected_option_id: selectedOptionId});

      fetchInitialState.current = false;
    }
  }, [fetch, selectedOptionId]);

  if (!fetchInitialState.current && isLoading) {
    setIsLoading(false);
  }

  useEffect(() => {
    if (isDone) {
      return;
    }
    if (sendCount.current === 0) {
      sendCount.current += 1;
      analytics.action('email_verification_sent');
      action({email_send: true});
    }
  }, [action, emailSendCount, isDone, analytics]);

  useEffect(() => {
    if (emailJustResent) {
      setEmailResent(true);
    }
    if (emailConfirmed) {
      setEmailIsConfirmed(true);
    }
  }, [emailConfirmed, emailJustResent, isDone]);

  const resendButtonPressed = () => {
    if (isDone) {
      return;
    }

    analytics.action('requested_resend_email_verification');
    action({selected_option_id: EMAIL_RESEND});
    setSelectedOption(EMAIL_RESEND);
  };

  const noEmailAccessButtonPressed = () => {
    if (isDone) {
      return;
    }

    if (hasResend) {
      analytics.action('didnt_receive_email');
    } else {
      analytics.action('no_email_access');
    }
    events.emit({
      name: 'inline-data-action',
      data: {
        purpose: 'channel_selection',
        channelID: 'channel_email',
      },
      nonce,
    });

    action({selected_option_id: EMAIL_NO_ACCESS});
    setSelectedOption(EMAIL_NO_ACCESS);
  };

  useEffect(() => {
    if (isDone) {
      return;
    }

    if (emailIsConfirmed || emailSendCount === 0) {
      return;
    }

    const timeout = setTimeout(() => {
      fetch({
        poll: true,
      });
    }, POLL_MS);

    return () => clearTimeout(timeout);
  }, [isDone, emailIsConfirmed, fetch, emailSendCount]);

  useEffect(() => {
    if (isDone) {
      return;
    }

    if (emailIsConfirmed) {
      onDone({
        type: 'success',
        message: EMAIL_CONFIRMED,
        extra: {
          outcome: EMAIL_CONFIRMED,
        },
      });
    }
  }, [isDone, emailIsConfirmed, onDone]);
  const intl = useIntl();

  const noEmailAccessOption = {
    value: EMAIL_NO_ACCESS,
    label: hasResend
      ? intl.formatMessage(messages.didntReceiveOption)
      : intl.formatMessage(messages.noAccessOption),
    onPress: noEmailAccessButtonPressed,
  };

  const resendEmailOption = {
    value: EMAIL_RESEND,
    label: intl.formatMessage(messages.resend),
    onPress: resendButtonPressed,
  };

  // Show both buttons on initial load, hide resend button when email send count >= 2
  // Use emailJustResent to ensure pressed resend button doesn't hide after rerender
  const allOptions = [
    ...(!isDone && hasResend && !emailResent ? [] : [resendEmailOption]),
    noEmailAccessOption,
  ];

  if (isLoading) {
    // Mimic the position and look of the ChatButtonOptions with this TypingRow
    // to prevent the UI from jumping around when the options are rendered
    return (
      <>
        <view.div
          css={{alignX: 'end', margin: 'small'}}
          data-testid="email-verification-typing-indicator"
          aria-live="off"
        >
          <TypingRow position="end" />
        </view.div>
      </>
    );
  }

  // Hide Options if email is confirmed and no option is selected
  // This is needed because for inline-data that are done and confirmed,
  // we still wan't to show their selected disabled option instead of hiding all options
  return (
    <>
      <view.div css={{margin: 'small'}}>
        {!emailIsConfirmed || selectedOption ? (
          <ChatButtonOptions
            options={allOptions}
            selectedOptionId={selectedOption}
          />
        ) : null}
      </view.div>
    </>
  );
};
